import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Clock, User as UserIcon, X } from "lucide-react";
import { SUB_STATUSES, subMeta } from "../lib/pipeline";

/**
 * Timeline Slot Builder — admin control that lists every scheduled slot on
 * a job and lets the receptionist add / delete more. Each slot ties an
 * installer to a stage for a defined start/end window.
 *
 * Props:
 *   job       : the unified job document (needs id + timeline array)
 *   onChange  : callback fired after any mutation succeeds; parent should reload the job
 */
export default function TimelineSlotsPanel({ job, onChange }) {
  const [installers, setInstallers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then(({ data }) => setInstallers(data.filter((u) => u.role === "installer" || u.role === "assistant"))).catch(() => {});
  }, []);

  const slots = (job.timeline || []).slice().sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));

  // Partition installers by whether they can do the picked stage. Staff with
  // no `capable_stages` list (unset / legacy) are treated as universal — they
  // stay in the qualified bucket so admins don't get an empty picker on day 1.
  const qualified = installers.filter((u) => !u.capable_stages || u.capable_stages.length === 0 || u.capable_stages.includes(form.stage));
  const others = installers.filter((u) => u.capable_stages && u.capable_stages.length > 0 && !u.capable_stages.includes(form.stage));

  const submit = async () => {
    if (!form.installer_id) return toast.error("Pick an installer");
    if (!form.start_at || !form.end_at) return toast.error("Pick start and end times");
    if (form.end_at <= form.start_at) return toast.error("End must be after start");
    setBusy(true);
    try {
      const inst = installers.find((u) => u.id === form.installer_id);
      await api.post(`/jobs/${job.id}/timeline`, {
        stage: form.stage,
        installer_id: form.installer_id,
        installer_name: inst?.name || inst?.email || "",
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        notes: form.notes,
        status: "pending",
      });
      toast.success(`Assigned ${inst?.name || "installer"} to ${subMeta("job", form.stage).label}`);
      setForm(blank());
      setAdding(false);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entryId) => {
    if (!window.confirm("Remove this time slot?")) return;
    try {
      await api.delete(`/jobs/${job.id}/timeline/${entryId}`);
      toast.success("Slot removed");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3" data-testid={`timeline-panel-${job.id}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-label flex items-center gap-1.5"><Clock className="w-3 h-3" /> Crew schedule</div>
        {!adding && (
          <button
            data-testid={`timeline-add-${job.id}`}
            onClick={() => setAdding(true)}
            className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 rounded-full px-2.5 py-1"
          >
            <Plus className="w-3 h-3" /> Add slot
          </button>
        )}
      </div>

      {/* Existing slots */}
      {slots.length === 0 && !adding && (
        <div className="text-xs text-slate-500 italic py-1">No crew assignments yet.</div>
      )}
      <div className="space-y-1.5">
        {slots.map((s) => (
          <div
            key={s.id}
            data-testid={`timeline-slot-${s.id}`}
            className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-xl px-3 py-2 text-xs"
          >
            <span className="pill pill-info text-[10px] shrink-0">{subMeta("job", s.stage).label}</span>
            <div className="flex-1 min-w-0">
              <div className="text-white truncate flex items-center gap-1.5">
                <UserIcon className="w-3 h-3 text-slate-400" />
                {s.installer_name || s.installer_id}
              </div>
              <div className="text-slate-500 text-[10px]">
                {fmt(s.start_at)} → {fmt(s.end_at)}
                {s.notes ? ` · ${s.notes}` : ""}
              </div>
            </div>
            <button
              data-testid={`timeline-slot-delete-${s.id}`}
              onClick={() => remove(s.id)}
              className="p-1 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10"
              title="Remove slot"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Add-slot form */}
      {adding && (
        <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid={`timeline-form-${job.id}`}>
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2 md:col-span-1">
              <span className="text-label block mb-1">Installer</span>
              <select
                data-testid={`timeline-installer-${job.id}`}
                value={form.installer_id}
                onChange={(e) => setForm({ ...form, installer_id: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
              >
                <option value="" className="bg-neutral-900">— pick —</option>
                {qualified.length > 0 && (
                  <optgroup label="Qualified for this stage" className="bg-neutral-900">
                    {qualified.map((u) => (
                      <option key={u.id} value={u.id} className="bg-neutral-900">
                        {u.name || u.email}{u.role === "assistant" ? " · assistant" : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
                {others.length > 0 && (
                  <optgroup label="Not trained on this stage" className="bg-neutral-900">
                    {others.map((u) => (
                      <option key={u.id} value={u.id} className="bg-neutral-900">
                        {u.name || u.email}{u.role === "assistant" ? " · assistant" : ""} (unqualified)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>
            <label className="col-span-2 md:col-span-1">
              <span className="text-label block mb-1">Stage</span>
              <select
                data-testid={`timeline-stage-${job.id}`}
                value={form.stage}
                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
              >
                {SUB_STATUSES.job.map((s) => (
                  <option key={s.value} value={s.value} className="bg-neutral-900">{s.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-label block mb-1">Start</span>
              <input
                data-testid={`timeline-start-${job.id}`}
                type="datetime-local"
                value={form.start_at}
                onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
              />
            </label>
            <label>
              <span className="text-label block mb-1">End</span>
              <input
                data-testid={`timeline-end-${job.id}`}
                type="datetime-local"
                value={form.end_at}
                onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white"
              />
            </label>
          </div>
          <input
            data-testid={`timeline-notes-${job.id}`}
            type="text"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes (optional)"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder-slate-500"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              data-testid={`timeline-cancel-${job.id}`}
              onClick={() => { setAdding(false); setForm(blank()); }}
              className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-full px-3 py-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              data-testid={`timeline-save-${job.id}`}
              onClick={submit}
              disabled={busy}
              className="text-[10px] bg-white text-black font-semibold rounded-full px-3 py-1 disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save slot"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function blank() {
  // default the datetime pickers to "now" so the admin only has to nudge them
  const nowLocal = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const start = nowLocal();
  return { installer_id: "", stage: "wrapping", start_at: start, end_at: start, notes: "" };
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
